import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Check,
  Info,
  Globe,
  Mail,
  Eye,
  EyeOff,
  Building,
  Plus,
  Trash2,
  Copy,
  Lock,
  Loader2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import tenantService from "../../services/tenant.service";

// Lightweight custom dropdown (no react-hook-form dependency)
interface SimpleDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}

const SimpleDropdown: React.FC<SimpleDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select an option",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-left flex items-center justify-between focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none text-sm text-slate-900 transition-all ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-slate-300"
        }`}
      >
        <span className={selected ? "text-slate-900" : "text-slate-400"}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                opt.value === value
                  ? "bg-blue-50 text-blue-700 font-semibold"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const steps = [
  { id: 1, name: "Basic Info", icon: Info },
  { id: 2, name: "Domain & Access", icon: Globe },
  { id: 3, name: "Email", icon: Mail },
  { id: 4, name: "Review", icon: Check },
];

const TenantFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [currentStep, setCurrentStep] = useState(1);
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nameAvailability, setNameAvailability] = useState<{ available: boolean; message: string } | null>(null);
  const [keyAvailability, setKeyAvailability] = useState<{ available: boolean; message: string } | null>(null);
  const nameCheckRef = useRef<ReturnType<typeof setTimeout>>();
  const keyCheckRef = useRef<ReturnType<typeof setTimeout>>();

  const handleCopyTenantKey = async () => {
    if (!formData.tenant_key) {
      toast.error("No tenant key to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(formData.tenant_key);
      setCopiedKey(true);
      toast.success("Tenant key copied to clipboard");
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const [formData, setFormData] = useState<any>({
    name: "",
    tenant_key: "",
    status: "ACTIVE",
    allowed_domains: [],
    domains: [{ admin_url: "", api_url: "" }],
    email_config: {
      provider: "smtp",
      host_name: "",
      host_port: 587,
      client_id: "",
      client_secret: "",
      from: "",
    },
  });

  useEffect(() => {
    if (!isEditing || !id) return;
    tenantService.getTenantByKey(id).then((res) => {
      const t = res.data;
      setFormData({
        name: t.name || "",
        tenant_key: t.tenant_key || "",
        status: t.status || "ACTIVE",
        allowed_domains: t.allowed_domains || [],
        domains: t.domains?.length ? t.domains : [{ admin_url: "", api_url: "" }],
        email_config: {
          provider: t.email_config?.provider || "smtp",
          host_name: t.email_config?.host_name || "",
          host_port: t.email_config?.host_port || 587,
          client_id: t.email_config?.client_id || "",
          client_secret: t.email_config?.client_secret || "",
          from: t.email_config?.from || "",
        },
      });
    }).catch(() => {
      toast.error("Failed to load tenant");
      navigate("/enterprise/tenants");
    });
  }, [id, isEditing]);

  const checkNameAvailability = (name: string) => {
    if (!name || isEditing) return;
    setNameAvailability(null);
    if (nameCheckRef.current) clearTimeout(nameCheckRef.current);
    nameCheckRef.current = setTimeout(async () => {
      try {
        const res = await tenantService.checkNameAvailability(name);
        setNameAvailability(res.data);
      } catch {}
    }, 500);
  };

  const checkKeyAvailability = (key: string) => {
    if (!key || isEditing) return;
    setKeyAvailability(null);
    if (keyCheckRef.current) clearTimeout(keyCheckRef.current);
    keyCheckRef.current = setTimeout(async () => {
      try {
        const res = await tenantService.checkKeyAvailability(key);
        setKeyAvailability(res.data);
      } catch {}
    }, 500);
  };

  const handleNext = () => setCurrentStep((prev) => Math.min(prev + 1, 4));
  const handleBack = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const toggleSecret = (field: string) => {
    setShowSecret((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const updateNestedField = (section: string, field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const addDomain = () => {
    setFormData((prev: any) => ({
      ...prev,
      allowed_domains: [...prev.allowed_domains, ""],
    }));
  };

  const updateDomain = (index: number, value: string) => {
    const newDomains = [...formData.allowed_domains];
    newDomains[index] = value;
    setFormData((prev: any) => ({
      ...prev,
      allowed_domains: newDomains,
    }));
  };

  const removeDomain = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      allowed_domains: prev.allowed_domains.filter(
        (_: any, i: number) => i !== index,
      ),
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        tenant_key: formData.tenant_key,
        status: formData.status,
        allowed_domains: formData.allowed_domains,
        domains: formData.domains,
        email_config: formData.email_config,
      };
      if (isEditing && id) {
        await tenantService.updateTenant(id, payload);
        toast.success("Tenant updated successfully");
      } else {
        await tenantService.createTenant(payload);
        toast.success("Tenant created successfully");
      }
      navigate("/enterprise/tenants");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save tenant");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <section className="bg-white rounded-md border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-blue-50 text-blue-600 rounded-md">
            <Info className="w-5 h-5" />
          </div>
          <h4 className="font-bold text-slate-900">Basic Information</h4>
        </div>
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Tenant Name
            </label>
            <input
              type="text"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 transition-all outline-none text-slate-900"
              placeholder="e.g. ACME Corporation"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                checkNameAvailability(e.target.value);
              }}
            />
            {!isEditing && nameAvailability && (
              <div className={`mt-1 flex items-center gap-1 text-xs ${nameAvailability.available ? "text-emerald-600" : "text-red-500"}`}>
                {nameAvailability.available
                  ? <CheckCircle2 className="w-3.5 h-3.5" />
                  : <XCircle className="w-3.5 h-3.5" />}
                {nameAvailability.message}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                Tenant Key
                {isEditing && (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded text-[10px] font-bold">
                    <Lock className="w-2.5 h-2.5" />
                    LOCKED
                  </span>
                )}
              </label>
            </div>
            <div className="relative">
              <input
                type="text"
                disabled={isEditing}
                className="w-full px-4 py-2.5 pr-20 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 transition-all outline-none text-slate-900 disabled:opacity-70 disabled:bg-slate-100 font-mono text-sm"
                placeholder="e.g. acme_corp"
                value={formData.tenant_key}
                onChange={(e) => {
                  const val = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "");
                  setFormData({ ...formData, tenant_key: val });
                  checkKeyAvailability(val);
                }}
              />
              {/* Copy button */}
              <button
                type="button"
                onClick={handleCopyTenantKey}
                disabled={!formData.tenant_key}
                title="Copy tenant key"
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-all ${
                  !formData.tenant_key
                    ? "text-slate-300 cursor-not-allowed"
                    : copiedKey
                      ? "text-emerald-600 bg-emerald-50"
                      : "text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                }`}
              >
                {copiedKey ? (
                  <Check className="w-4 h-4" strokeWidth={3} />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
            {!isEditing && keyAvailability && (
              <div className={`mt-1 flex items-center gap-1 text-xs ${keyAvailability.available ? "text-emerald-600" : "text-red-500"}`}>
                {keyAvailability.available
                  ? <CheckCircle2 className="w-3.5 h-3.5" />
                  : <XCircle className="w-3.5 h-3.5" />}
                {keyAvailability.message}
              </div>
            )}

            {/* Help note - prominent warning */}
            <div className="mt-1 flex items-start">
              <div className="text-xs text-gray-400 leading-relaxed">
                <span className="font-semibold">
                  Cannot be changed after creation.
                </span>{" "}
                This key is used as the database name. Use lowercase letters,
                numbers, underscores, and hyphens only.
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Status
            </label>
            <SimpleDropdown
              value={formData.status}
              onChange={(val) => setFormData({ ...formData, status: val })}
              placeholder="Select tenant status"
              options={[
                { value: "ACTIVE", label: "Active" },
                { value: "INACTIVE", label: "Inactive" },
                { value: "SUSPENDED", label: "Suspended" },
              ]}
            />
          </div>
        </div>
      </section>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <section className="bg-white rounded-md border border-slate-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-md">
              <Globe className="w-5 h-5" />
            </div>
            <h4 className="font-bold text-slate-900">Domain & Access Control</h4>
          </div>
          {isEditing && (
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded text-xs font-bold">
              <Lock className="w-3 h-3" />
              Read-only in edit mode
            </span>
          )}
        </div>

        {isEditing && (
          <div className="mb-5 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
            <Lock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">
              Domain & Access settings cannot be changed after tenant creation.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Admin URL
              </label>
              <input
                type="text"
                disabled={isEditing}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 transition-all outline-none text-slate-900 text-sm disabled:opacity-70 disabled:bg-slate-100 disabled:cursor-not-allowed"
                placeholder="https://admin.acme.dociq.com"
                value={formData.domains[0]?.admin_url || ""}
                onChange={(e) => {
                  const newDomains = [...formData.domains];
                  newDomains[0] = { ...(newDomains[0] || {}), admin_url: e.target.value };
                  setFormData({ ...formData, domains: newDomains });
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                API URL
              </label>
              <input
                type="text"
                disabled={isEditing}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 transition-all outline-none text-slate-900 text-sm disabled:opacity-70 disabled:bg-slate-100 disabled:cursor-not-allowed"
                placeholder="https://api.acme.dociq.com"
                value={formData.domains[0]?.api_url || ""}
                onChange={(e) => {
                  const newDomains = [...formData.domains];
                  newDomains[0] = { ...(newDomains[0] || {}), api_url: e.target.value };
                  setFormData({ ...formData, domains: newDomains });
                }}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="block text-sm font-semibold text-slate-700">
                  Allowed Web Domains (CORS)
                </label>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Specify allowed web domain URLs for CORS access control. Click
                  "Add Domain" to restrict access.
                </p>
              </div>
              {!isEditing && (
                <button
                  type="button"
                  onClick={addDomain}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-md hover:bg-slate-700 transition-all shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Domain
                </button>
              )}
            </div>

            <div className="space-y-3 mt-4">
              {formData.allowed_domains.map((domain: string, idx: number) => (
                <div key={idx} className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Additional Domain {idx + 1}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      disabled={isEditing}
                      className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 transition-all outline-none text-slate-900 text-sm disabled:opacity-70 disabled:bg-slate-100 disabled:cursor-not-allowed"
                      placeholder="https://example.com"
                      value={domain}
                      onChange={(e) => updateDomain(idx, e.target.value)}
                    />
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => removeDomain(idx)}
                        className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all border border-transparent hover:border-red-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {formData.allowed_domains.length === 0 && (
                <div className="py-8 border-2 border-dashed border-slate-100 rounded-xl flex flex-col items-center justify-center bg-slate-50/50">
                  <Globe className="w-8 h-8 text-slate-200 mb-2" />
                  <p className="text-xs text-slate-400 font-medium">
                    No additional domains added
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <section className="bg-white rounded-md border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-amber-50 text-amber-600 rounded-md">
            <Mail className="w-5 h-5" />
          </div>
          <h4 className="font-bold text-slate-900">
            Email Configuration (SMTP/SES)
          </h4>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Provider
            </label>
            <SimpleDropdown
              value={formData.email_config.provider}
              onChange={(val) =>
                updateNestedField("email_config", "provider", val)
              }
              placeholder="Select email provider"
              options={[
                { value: "smtp", label: "SMTP" },
                { value: "ses", label: "AWS SES" },
                { value: "sendgrid", label: "SendGrid" },
                { value: "mailgun", label: "Mailgun" },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              From Email Address
            </label>
            <input
              type="email"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none transition-all text-sm text-slate-900"
              placeholder="no-reply@example.com"
              value={formData.email_config.from}
              onChange={(e) =>
                updateNestedField("email_config", "from", e.target.value)
              }
            />
          </div>
          <div className="col-span-2 grid grid-cols-4 gap-4">
            <div className="col-span-3">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Host
              </label>
              <input
                type="text"
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none transition-all text-sm text-slate-900"
                placeholder="smtp.sendgrid.net"
                value={formData.email_config.host_name}
                onChange={(e) =>
                  updateNestedField("email_config", "host_name", e.target.value)
                }
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Port
              </label>
              <input
                type="number"
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none transition-all text-sm text-slate-900"
                placeholder="587"
                value={formData.email_config.host_port}
                onChange={(e) =>
                  updateNestedField(
                    "email_config",
                    "host_port",
                    parseInt(e.target.value),
                  )
                }
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Client ID / Username
            </label>
            <input
              type="text"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none transition-all text-sm text-slate-900"
              placeholder="e.g. apikey or user@acme.com"
              value={formData.email_config.client_id}
              onChange={(e) =>
                updateNestedField("email_config", "client_id", e.target.value)
              }
            />
          </div>
          <div className="relative">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Client Secret / API Key
            </label>
            <input
              type={showSecret.email_secret ? "text" : "password"}
              className="w-full px-4 py-2.5 pr-10 bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-300 outline-none transition-all text-sm text-slate-900"
              placeholder="Enter client secret or API key"
              value={formData.email_config.client_secret}
              onChange={(e) =>
                updateNestedField(
                  "email_config",
                  "client_secret",
                  e.target.value,
                )
              }
            />
            <button
              type="button"
              onClick={() => toggleSecret("email_secret")}
              className="absolute right-3 top-[36px] text-slate-400 hover:text-slate-600"
            >
              {showSecret.email_secret ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </section>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <section className="bg-white rounded-md border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-emerald-50 text-emerald-600 rounded-md">
            <Check className="w-5 h-5" />
          </div>
          <h4 className="font-bold text-slate-900">Review Configuration</h4>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h5 className="text-sm font-bold text-blue-900">
                Review Information
              </h5>
              <p className="text-xs text-blue-700 mt-1">
                Please review all tenant configuration fields before proceeding.
                All sensitive data will be encrypted at rest.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Tenant Name
            </span>
            <p className="text-sm font-medium text-slate-900">
              {formData.name}
            </p>
          </div>
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Tenant Key
            </span>
            <p className="text-sm font-medium text-slate-900">
              {formData.tenant_key}
            </p>
          </div>
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Admin URL
            </span>
            <p className="text-sm font-medium text-slate-900">
              {formData.domains[0]?.admin_url || "—"}
            </p>
          </div>
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Status
            </span>
            <p className="text-sm font-medium text-slate-900 capitalize">
              {formData.status}
            </p>
          </div>
        </div>

        <div className="pt-6 border-t border-slate-100 flex flex-col gap-3">
          <div className="flex items-center gap-3 text-slate-600">
            <Check className="w-4 h-4 text-emerald-500" />
            <span className="text-xs">
              Infrastructure provisioning will trigger automatically
            </span>
          </div>
          <div className="flex items-center gap-3 text-slate-600">
            <Check className="w-4 h-4 text-emerald-500" />
            <span className="text-xs">
              Database migration for '{formData.tenant_key}' will be initialized
            </span>
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 -my-4 -mx-4 sm:-mx-8 lg:-mx-4">
      {/* Sticky Header with Stepper - sits below app header (max 72px) */}
      <div className="sticky top-[72px] z-40 bg-white border-b border-slate-200 shadow-sm">
        {/* Top Bar - Compact */}
        <div className="px-6 py-2.5 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/enterprise/tenants")}
              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-md transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5 text-blue-600" />
              <h1 className="text-base font-bold text-slate-900">
                {isEditing ? "Edit Tenant" : "Create New Tenant"}
              </h1>
              <span className="text-xs text-slate-400">
                · {formData.name || "New Tenant"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold text-slate-500 bg-slate-50 px-2.5 py-1 rounded-full border border-slate-200">
              {Math.round((currentStep / 4) * 100)}%
            </div>
            <button
              onClick={() => navigate("/enterprise/tenants")}
              className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 rounded-md border border-slate-200 transition-all"
            >
              Discard
            </button>
          </div>
        </div>

        {/* Horizontal Stepper - Compact */}
        <div className="px-6 py-3">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {steps.map((step, idx) => {
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              const isClickable = step.id <= currentStep;
              const StepIcon = step.icon;

              return (
                <React.Fragment key={step.id}>
                  <button
                    type="button"
                    disabled={!isClickable}
                    onClick={() => isClickable && setCurrentStep(step.id)}
                    className={`flex items-center gap-2 ${
                      isClickable ? "cursor-pointer" : "cursor-not-allowed"
                    }`}
                  >
                    {/* Step Circle */}
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center transition-all text-xs font-bold ${
                        isActive
                          ? "bg-blue-600 text-white"
                          : isCompleted
                            ? "bg-emerald-500 text-white"
                            : "bg-slate-100 text-slate-400 border border-slate-200"
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-3.5 h-3.5" strokeWidth={3} />
                      ) : (
                        <StepIcon className="w-3.5 h-3.5" />
                      )}
                    </div>

                    {/* Step Label - inline */}
                    <span
                      className={`text-xs font-semibold whitespace-nowrap ${
                        isActive
                          ? "text-slate-900"
                          : isCompleted
                            ? "text-slate-600"
                            : "text-slate-400"
                      }`}
                    >
                      {step.name}
                    </span>
                  </button>

                  {/* Connector Line */}
                  {idx < steps.length - 1 && (
                    <div className="flex-1 h-[2px] mx-3 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          currentStep > step.id
                            ? "bg-emerald-500 w-full"
                            : "bg-transparent w-0"
                        }`}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form Content - section cards stand alone with shadow-sm */}
      <div className="p-4 pb-[232px]">
        <div className="min-h-[450px]">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep5()}
        </div>
      </div>

      {/* Sticky Footer Navigation - modern floating bar */}
      <div className="sticky bottom-0 z-30 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-[0_-4px_20px_-2px_rgba(0,0,0,0.06)]">
        {/* Thin progress bar at top */}
        <div className="h-1 bg-slate-100 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / 4) * 100}%` }}
          />
        </div>

        <div className="px-6 py-3 flex items-center justify-between">
          {/* Previous Button */}
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="group flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 rounded-md border border-slate-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-slate-200 transition-all"
          >
            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5 group-disabled:transform-none" />
            Previous
          </button>

          {/* Center: Animated step dots */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              {steps.map((step) => {
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                return (
                  <div
                    key={step.id}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      isActive
                        ? "w-8 bg-blue-600"
                        : isCompleted
                          ? "w-2 bg-emerald-500"
                          : "w-2 bg-slate-200"
                    }`}
                  />
                );
              })}
            </div>
            <span className="text-xs font-semibold text-slate-500 tabular-nums">
              <span className="text-slate-900">{currentStep}</span>
              <span className="text-slate-300 mx-1">/</span>
              {steps.length}
            </span>
          </div>

          {/* Next / Submit Button */}
          <div className="flex items-center gap-3">
            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                className="group flex items-center gap-2 pl-5 pr-4 py-2 bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-md hover:from-slate-800 hover:to-slate-700 transition-all text-sm font-semibold shadow-sm hover:shadow-md"
              >
                Next Step
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 group-hover:bg-white/20 transition-all">
                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="group flex items-center gap-2 pl-5 pr-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-md hover:from-blue-700 hover:to-blue-800 transition-all text-sm font-semibold shadow-sm shadow-blue-200 hover:shadow-md hover:shadow-blue-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Saving..." : isEditing ? "Save Configuration" : "Create Tenant"}
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 group-hover:bg-white/20 transition-all">
                  {submitting
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantFormPage;
